import React, { useEffect, useRef } from "react";
import * as echarts from "echarts/core";
import { TooltipComponent, LegendComponent } from "echarts/components";
import { PieChart } from "echarts/charts";
import { CanvasRenderer } from "echarts/renderers";
import { LabelLayout } from "echarts/features"; // ✅ needed for label lines
import {
  fixedPercentage,
  formatNumberCommaSeparated,
  truncateNumber,
} from "../../utils/common";
import { Box, Typography } from "@mui/material";

echarts.use([TooltipComponent, LegendComponent, PieChart, CanvasRenderer, LabelLayout]);

const EchartDoughnutChart = ({
  labels,
  dataPoints,
  colors,
  legendPosition = "top",
  customWidth,
  customHeight,
  hide,
  mt,
  /** When set, used for slice values and percentages (e.g. scientific for tiny numbers) */
  formatDisplayValue,
  showSliceLabels = true,
  innerRadius = "40%",
  outerRadius,
  sliceGap = 3,
  sliceBorderWidth = 0,
  /** When true, tooltip uses appendToBody + wrap + smart position so long slice names are not clipped */
  fixTooltipClipping = false,
}) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !labels?.length || !dataPoints?.length || !colors?.length) {
      return;
    }

    const chart = echarts.init(chartRef.current);
    const total = dataPoints.reduce((a, v) => Number(a) + Number(v), 0);
    const SMALL_PCT = 6;
    const fmt =
      typeof formatDisplayValue === "function"
        ? formatDisplayValue
        : (v) => formatNumberCommaSeparated(truncateNumber(v));
    const fmtPct = (v) =>
      typeof formatDisplayValue === "function"
        ? formatDisplayValue(v, 2)
        : truncateNumber(v, 2);

    // build data with per-slice config
    const seriesData = labels.map((name, i) => {
      const value = Number(dataPoints[i] ?? 0);
      const pct = total ? (value / total) * 100 : 0;
      const isSmall = pct < SMALL_PCT;

      return {
        value,
        name,
        itemStyle: { color: colors[i] },
        label: {
          show: showSliceLabels,
          position: isSmall ? "outside" : "inside",
          color: isSmall ? "#000" : "#fff",
          fontSize: 10,
          fontWeight: "bold",
          formatter: (p) => {
            const per = fixedPercentage((Number(p.value) / total) * 100);
            return `${fmtPct(per)}%`;
          },
        },
        labelLine: !showSliceLabels
          ? { show: false }
          : isSmall
          ? {
              show: true,
              length: 8,
              length2: 6,
              smooth: true,
              lineStyle: { width: 1 },
            }
          : { show: false },
      };
    });

    const hasSmall = seriesData.some(
      (s) => s.label?.position === "outside" && s.label?.show
    );
    const computedOuterRadius = outerRadius || (hasSmall ? "78%" : "90%");

    const tooltipFormatter = (p) => {
      const percentage = fixedPercentage((Number(p.value) / total) * 100);
      return `${p.name}: ${fmt(p.value)} (${fmtPct(percentage)}%)`;
    };

    const tooltipBase = {
      trigger: "item",
      formatter: tooltipFormatter,
    };

    const tooltip =
      fixTooltipClipping === true
        ? {
            ...tooltipBase,
            appendToBody: true,
            confine: false,
            extraCssText:
              "max-width:min(420px,90vw);white-space:normal;word-break:break-word;text-align:left;line-height:1.45;padding:10px 12px;box-sizing:border-box;",
            position(point, _params, _dom, _rect, size) {
              const x = point[0];
              const y = point[1];
              const bw = size.contentSize[0];
              const bh = size.contentSize[1];
              const vw = size.viewSize[0];
              const vh = size.viewSize[1];
              const pad = 8;
              const offset = 14;
              // Prefer to the right of cursor so labels are not cut off on the left edge of the card
              let left = x + offset;
              let top = y - bh / 2;
              if (left + bw > vw - pad) left = Math.max(pad, x - bw - offset);
              if (left < pad) left = pad;
              if (top < pad) top = pad;
              if (top + bh > vh - pad) top = Math.max(pad, vh - bh - pad);
              return [left, top];
            },
          }
        : tooltipBase;

    const option = {
      tooltip,
      series: [
        {
          type: "pie",
          radius: [innerRadius, computedOuterRadius],
          center: ["50%", "50%"],
          avoidLabelOverlap: true,
          minShowLabelAngle: 0,
          padAngle: sliceGap,
          itemStyle: {
            borderRadius: 4,
            borderColor: "#ffffff",
            borderWidth: sliceBorderWidth,
          },
          labelLine: { show: showSliceLabels },
          data: seriesData,
        },
      ],
    };

    chart.setOption(option);
    const onResize = () => chart.resize();
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      chart.dispose();
    };
  }, [
    labels,
    dataPoints,
    colors,
    legendPosition,
    formatDisplayValue,
    showSliceLabels,
    innerRadius,
    outerRadius,
    sliceGap,
    sliceBorderWidth,
    fixTooltipClipping,
  ]);

  return (
    <Box>
      {labels?.length > 0 && dataPoints?.length > 0 ? (
        <div
          ref={chartRef}
          style={{
            width: customWidth || "100%",
            height: customHeight || "100%",
            aspectRatio: "1",
            padding: "0px",
          }}
        />
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: customHeight || "100%",
            width: "100%",
            mt: mt,
          }}
        >
          <Typography
            sx={{
              fontSize: "24px",
              fontFamily: "inter",
              color: "#d3d3d3",
              fontWeight: "bold",
            }}
          >
            {!hide && "No Data Found"}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default EchartDoughnutChart;
